import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Theme,
  Tooltip,
} from '@ublend-npm/aulaui-core';
import React from 'react';
import { VolumeOffOutlined, VolumeUpOutlined } from '@mui/icons-material';
import { createStyles } from './ActionConfirmation.styles';

type SelectedId = 'mute-students' | 'unmute-students';

type Props = {
  selectedId: SelectedId;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
  theme: Theme;
  isDesktop: boolean;
};

const MUTE_STUDENTS = {
  title: 'Mute posts by students',
  button: 'Mute students',
  icon: <VolumeOffOutlined />,
  styles: {
    light: {
      backgroundColor: '#EF6C004D',
      color: '#EF6C00',
    },
    dark: {
      backgroundColor: '#FFA7264D',
      color: '#FFA726',
    },
  },
  description:
    'You’ll no longer receive notifications when students post in this space, but you will still be able to see their posts. You can unmute them at any time.',
};

const UNMUTE_STUDENTS = {
  title: 'Unmute posts by students',
  button: 'Unmute students',
  icon: <VolumeUpOutlined />,
  styles: {
    light: {
      backgroundColor: '#2E7D324D',
      color: '#2E7D32',
    },
    dark: {
      backgroundColor: '#66BB6A4D',
      color: '#66BB6A',
    },
  },
  description:
    'You’ll start receiving notifications when students post in this space again. You can mute them at any time.',
};

const ActionConfirmation = ({
  selectedId,
  open,
  saving,
  onClose,
  onConfirm,
  theme,
  isDesktop,
}: Props) => {
  const styles = createStyles(theme);
  const isDark = theme.palette.mode === 'dark';

  const modalItems =
    selectedId === 'mute-students' ? MUTE_STUDENTS : UNMUTE_STUDENTS;

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby="confirm-student-notifications"
    >
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.title}>{modalItems.title}</Box>
          <IconButton
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            {isDesktop ? (
              <Tooltip title="Close">
                <Icons.Close />
              </Tooltip>
            ) : (
              <Icons.Close />
            )}
          </IconButton>
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.iconWrapper}>
            <Box
              sx={{
                ...styles.iconBackground,
                ...(isDark ? modalItems.styles.dark : modalItems.styles.light),
              }}
            >
              {modalItems.icon}
            </Box>
          </Box>
          <Box sx={styles.description}>{modalItems.description}</Box>
        </Box>
        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            type="button"
            onClick={onClose}
            disabled={saving}
            sx={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            kind="primary"
            type="button"
            onClick={onConfirm}
            disabled={saving}
            loading={saving}
            sx={styles.confirmButton}
          >
            {modalItems.button}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ActionConfirmation;
